import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogIn, ShieldAlert, Inbox, Loader2, Trash2, Mail, Phone, Clock } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusStyles = {
  new: "bg-[#D80000]/10 text-[#D80000]",
  contacted: "bg-amber-100 text-amber-700",
  closed: "bg-emerald-100 text-emerald-700",
};

const QuoteCard = ({ quote, onStatusChange, onDelete }) => (
  <div className="bg-white rounded-lg shadow-sm p-5">
    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="font-heading font-semibold text-[#1a1a1a] text-lg">{quote.name}</p>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyles[quote.status] || statusStyles.new}`}>
            {quote.status || "new"}
          </span>
          {quote.interest && (
            <span className="text-xs bg-gray-100 text-[#1a1a1a]/70 px-2.5 py-1 rounded-full">{quote.interest}</span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-2 text-sm text-[#1a1a1a]/70 flex-wrap">
          <a href={`mailto:${quote.email}`} className="flex items-center gap-1.5 hover:text-[#D80000]"><Mail className="w-4 h-4" /> {quote.email}</a>
          {quote.phone && <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> {quote.phone}</span>}
          <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {new Date(quote.created_at).toLocaleString()}</span>
        </div>
        <p className="text-[#1a1a1a]/80 mt-3 leading-relaxed">{quote.message}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Select value={quote.status || "new"} onValueChange={(v) => onStatusChange(quote.id, v)}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={() => onDelete(quote.id)} className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-gray-200 text-[#1a1a1a]/60 hover:border-[#D80000] hover:text-[#D80000]" aria-label="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);

const Admin = () => {
  const { user, loading, login } = useAuth();
  const { toast } = useToast();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [busy, setBusy] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [q, s] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setQuotes(q.data || []);
      setStats(s.data || {});
    } catch (e) {
      setQuotes([]);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) loadData();
    else setBusy(false);
  }, [user, loadData]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/quotes/${id}`, { status }, { withCredentials: true });
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
      const s = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(s.data);
    } catch (e) {
      toast({ title: "Could not update status" });
    }
  };

  const removeQuote = async (id) => {
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, { withCredentials: true });
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      toast({ title: "Quote deleted" });
    } catch (e) {
      toast({ title: "Could not delete" });
    }
  };

  if (loading || busy) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D80000] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-5">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D80000]/10 mb-5">
          <LogIn className="w-7 h-7 text-[#D80000]" />
        </span>
        <h1 className="font-heading text-3xl font-semibold text-[#1a1a1a]">Admin Login</h1>
        <p className="text-[#1a1a1a]/70 mt-3 max-w-md">Sign in with your admin Google account to access the quote inbox.</p>
        <button onClick={login} className="inline-flex items-center gap-2 mt-7 bg-[#D80000] text-white font-semibold px-7 py-3.5 rounded-md hover:bg-[#b00000]">
          <LogIn className="w-5 h-5" /> Sign in with Google
        </button>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-5">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-5">
          <ShieldAlert className="w-7 h-7 text-amber-600" />
        </span>
        <h1 className="font-heading text-3xl font-semibold text-[#1a1a1a]">Not Authorized</h1>
        <p className="text-[#1a1a1a]/70 mt-3 max-w-md">
          Your account ({user.email}) doesn&apos;t have admin access. Ask the site
          owner to add your email to ADMIN_EMAILS.
        </p>
        <Link to="/" className="mt-6 text-[#D80000] font-semibold">Back to site</Link>
      </div>
    );
  }

  const statCards = [
    { label: "Total", value: stats.total, tone: "bg-[#1a1a1a] text-white" },
    { label: "New", value: stats.new, tone: "bg-[#D80000] text-white" },
    { label: "Contacted", value: stats.contacted, tone: "bg-amber-500 text-white" },
    { label: "Closed", value: stats.closed, tone: "bg-emerald-600 text-white" },
  ];

  return (
    <div className="bg-[#f7f7f8] min-h-[80vh]">
      <section className="bg-[#141414] py-12">
        <div className="max-w-[1240px] mx-auto px-5 flex items-center gap-3">
          <Inbox className="w-7 h-7 text-[#D80000]" />
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">Quote Inbox</h1>
            <p className="text-white/60 text-sm">Manage quote requests submitted through the site</p>
          </div>
        </div>
      </section>

      <section className="max-w-[1240px] mx-auto px-5 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.label} className={`rounded-lg p-5 ${c.tone}`}>
              <p className="font-heading text-3xl font-bold">{c.value}</p>
              <p className="text-sm opacity-90">{c.label}</p>
            </div>
          ))}
        </div>

        {quotes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-16 text-center text-[#1a1a1a]/60">
            No quote requests yet.
          </div>
        ) : (
          <div className="space-y-4">
            {quotes.map((q) => (
              <QuoteCard key={q.id} quote={q} onStatusChange={updateStatus} onDelete={removeQuote} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Admin;
